// src/pages/Dashboard.js - Updated with Cloud Function activity monitor
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, orderBy, limit, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";

const parseDateStr = (str) => {
  if (!str) return null;
  const parts = str.includes('/') ? str.split('/') : str.split('-');
  if (parts.length !== 3) return null;
  const [a, b, c] = parts.map(Number);
  return str.includes('/') ? new Date(c, a - 1, b) : new Date(a, b - 1, c);
};

const StatCard = ({ icon, label, value, sub, color = "#F97316" }) => (

  <div style={{ background: 'var(--surface)', border: `1px solid ${color}22`, borderRadius: 16, padding: 24, flex: 1, minWidth: 180 }}>
    <div style={{ fontSize: 28, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 32, fontWeight: 800, color, fontFamily: 'Syne' }}>{value}</div>
    <div style={{ color: 'var(--text)', fontWeight: 600, marginTop: 4 }}>{label}</div>
    {sub && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{sub}</div>}
  </div>
);

const ActivityItem = ({ icon, text, time, color }) => (

  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--surface2)' }}>
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ color: 'var(--text)', fontSize: 13 }}>{text}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{time}</div>
    </div>
  </div>
);

const FunctionLogItem = ({ log }) => {
  const fnConfig = {
    onReviewCreated: { icon: "⭐", color: "#F59E0B", label: "Rating Updated" },
    onHirerReviewCreated: { icon: "⭐", color: "#F59E0B", label: "Hirer Rating Updated" },
    onLicenseStatusChanged: { icon: "🪪", color: "#3B82F6", label: "License Email Sent" },
    scheduledLicenseExpiryCheck: { icon: "📅", color: "#EAB308", label: "Expiry Check Run" },
    scheduledMonthlyReset: { icon: "🔄", color: "#8B5CF6", label: "Monthly Reset" },
    onAccountDeletionRequested: { icon: "🗑️", color: "#EF4444", label: "Account Deleted" },
    onHireRequestCreated: { icon: "🤝", color: "#22C55E", label: "Hire Notification Sent" },
    onJobApplicationCreated: { icon: "📋", color: "#F97316", label: "Application Processed" },
  };
  const conf = fnConfig[log.function] || { icon: "⚡", color: "var(--text-secondary)", label: log.function };
  const timeAgo = (ts) => {
    if (!ts?.seconds) return "recently";
    const diff = Math.floor((Date.now() / 1000 - ts.seconds) / 60);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--surface2)" }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${conf.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
        {conf.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: conf.color, fontSize: 12, fontWeight: 700 }}>{conf.label}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
          {log.driverId && `Driver: ${log.driverId?.slice(0, 8)}...`}
          {log.driversChecked && `Checked: ${log.driversChecked} drivers · Suspended: ${log.suspended} · Warned: ${log.notified30 + log.notified7}`}
          {log.driverResets && `Reset: ${log.driverResets} drivers, ${log.hirerResets} hirers`}
          {log.deletedDocs && `Deleted: ${log.deletedDocs} documents`}
        </div>
      </div>
      <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{timeAgo(log.timestamp)}</div>
    </div>
  );
};

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState({
    totalDrivers: 0, pendingVerification: 0, pendingPhotos: 0,
    totalHirers: 0, pendingHirers: 0,
    totalJobs: 0, activeJobs: 0, overdueJobs: 0,
    totalApplications: 0, suspendedUsers: 0,
    pendingReports: 0,
    pendingAbstracts: 0, pendingAlerts: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [functionLogs, setFunctionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("signups"); // signups | functions

  useEffect(() => {
    const load = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const drivers = users.filter(u => u.role === "driver");
        const hirers = users.filter(u => u.role === "hirer");

        // Load pending admin alerts (declaration/system only — exclude doc-review submissions)
        try {
          const DOC_REVIEW_TYPES = new Set(['license_pending_review', 'abstract_pending_review', 'pow_pending_review']);
          const alertSnap = await getDocs(query(collection(db, 'adminAlerts'), where('resolved', '==', false)));
          const alertCount = alertSnap.docs.filter(d => !DOC_REVIEW_TYPES.has(d.data().type)).length;
          const declineSnap = await getDocs(query(collection(db, 'declarations'), where('result', '==', 'declined_suspended')));
          const totalAlerts = alertCount + declineSnap.size;
          setStats(prev => ({ ...prev, pendingAlerts: totalAlerts }));
        } catch (_) {}

        setStats({
          totalDrivers: drivers.length,
          pendingVerification: drivers.filter(u => u.licenseStatus === 'pending').length,
          pendingPhotos: drivers.filter(u => u.profilePicStatus === 'pending' || u.profilePicStatus === 'rejected').length,
          totalHirers: hirers.length,
          pendingHirers: hirers.filter(u => u.verificationStatus === 'pending').length,
          totalJobs: 0,
          activeJobs: 0,
          totalApplications: 0,
          suspendedUsers: users.filter(u => u.accountStatus === 'suspended').length,
          pendingAbstracts: drivers.filter(u => u.abstractStatus === 'pending').length,
        });

        const jobsSnap = await getDocs(collection(db, 'jobs'));
        const jobs = jobsSnap.docs.map(d => d.data());
        const appsSnap = await getDocs(collection(db, 'applications'));

        const now = new Date();
        const overdueCount = jobs.filter(j =>
          ['assigned', 'filled'].includes(j.status) &&
          (() => { const d = parseDateStr(j.bookingEndDate); return d && d < now; })()
        ).length;

        const reportsSnap = await getCountFromServer(
          query(collection(db, 'reports'), where('status', '==', 'pending'))
        );

        setStats(prev => ({
          ...prev,
          totalJobs: jobs.length,
          activeJobs: jobs.filter(j => j.status === 'open').length,
          overdueJobs: overdueCount,
          totalApplications: appsSnap.size,
          pendingReports: reportsSnap.data().count,
        }));

        const sorted = users
          .filter(u => u.createdAt)
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
          .slice(0, 8);
        setRecentUsers(sorted);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();

    // Re-fetch whenever admin switches back to this tab
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const loadLogs = async () => {
      setLogsLoading(true);
      try {
        const snap = await getDocs(collection(db, "functionLogs"));
        const logs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
          .slice(0, 20);
        setFunctionLogs(logs);
      } catch (err) { console.error(err); }
      finally { setLogsLoading(false); }
    };
    loadLogs();
  }, []);

  const timeAgo = (ts) => {
    if (!ts?.seconds) return "recently";
    const diff = Math.floor((Date.now() / 1000 - ts.seconds) / 60);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  if (loading) return <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>Loading dashboard…</div>;

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Dashboard</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4 }}>Live overview of your Crewboard platform</p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard icon="🧑‍✈️" label="Total Drivers" value={stats.totalDrivers} sub={`${stats.pendingVerification} pending verification`} color="#F97316" />
        <StatCard icon="🏢" label="Total Hirers" value={stats.totalHirers} sub={`${stats.pendingHirers} pending verification`} color="#3B82F6" />
        <StatCard icon="📋" label="Total Jobs" value={stats.totalJobs} sub={`${stats.activeJobs} currently open`} color="#22C55E" />
        <StatCard icon="📨" label="Applications" value={stats.totalApplications} sub="All time" color="#8B5CF6" />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard icon="⏳" label="Pending Licenses" value={stats.pendingVerification} sub="Awaiting your review" color="#EAB308" />
        <StatCard icon="📸" label="Photo Review" value={stats.pendingPhotos} sub="Pending + rejected photos" color="#A855F7" />
        <StatCard icon="⛔" label="Suspended Users" value={stats.suspendedUsers} sub="Active suspensions" color="#EF4444" />
        <StatCard icon="✅" label="Active Jobs" value={stats.activeJobs} sub="Open for applications" color="#22C55E" />
        <StatCard icon="🚨" label="Overdue Jobs" value={stats.overdueJobs} sub="Past end date, still assigned" color={stats.overdueJobs > 0 ? "#EF4444" : "#22C55E"} />
        <StatCard icon="🚨" label="Pending Reports" value={stats.pendingReports} sub="User reports awaiting review" color="#EF4444" />
      </div>

      {/* Alerts */}
      {stats.pendingVerification > 0 && (
        <div style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#EAB308', fontWeight: 600 }}>{stats.pendingVerification} driver license{stats.pendingVerification > 1 ? 's' : ''} awaiting verification</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Go to Driver Verification to review — approval/rejection emails are sent automatically by Cloud Functions</div>
          </div>
          <a href="/drivers" style={{ background: '#EAB308', color: '#000', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={e => { e.preventDefault(); onNavigate?.('drivers'); }}>Review →</a>
        </div>
      )}

      {stats.pendingPhotos > 0 && (
        <div style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>📸</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#A855F7', fontWeight: 600 }}>{stats.pendingPhotos} profile photo{stats.pendingPhotos > 1 ? 's' : ''} need attention — pending review or rejected</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Use the 📸 Photo Pending / Photo Rejected filters in Driver Verification to review</div>
          </div>
          <a href="/drivers" style={{ background: '#A855F7', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={e => { e.preventDefault(); onNavigate?.('drivers'); }}>Review →</a>
        </div>
      )}

      {stats.overdueJobs > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#EF4444', fontWeight: 600 }}>{stats.overdueJobs} job{stats.overdueJobs > 1 ? 's are' : ' is'} overdue — past end date, still assigned</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Hirer has not marked these complete. You can manually expire or complete them in Job Management.</div>
          </div>
          <a href="/jobs" style={{ background: '#EF4444', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={e => { e.preventDefault(); onNavigate?.('jobs'); }}>Review →</a>
        </div>
      )}

      {stats.pendingReports > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#EF4444', fontWeight: 600 }}>{stats.pendingReports} user report{stats.pendingReports > 1 ? 's' : ''} awaiting review</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Review and take action — warn, suspend, or dismiss reported users.</div>
          </div>
          <a href="/reports" style={{ background: '#EF4444', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={e => { e.preventDefault(); onNavigate?.('reports'); }}>Review →</a>
        </div>
      )}

      {/* Cloud Functions Status */}
      <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
        <div style={{ color: '#22C55E', fontWeight: 700, marginBottom: 8 }}>☁️ Cloud Functions Active</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            '⭐ Auto rating calc',
            '🪪 License emails',
            '📅 Daily expiry check',
            '🔄 Monthly reset',
            '🗑️ PIPEDA deletion',
            '🤝 Hire notifications',
            '📋 Application counter',
          ].map(f => (
            <span key={f} style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Activity Feed */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 16, padding: 24 }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--surface2)', borderRadius: 10, padding: 4 }}>
          {[
            { id: 'signups', label: '👥 Recent Signups' },
            { id: 'functions', label: '☁️ Function Logs' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, background: activeTab === tab.id ? '#F97316' : 'transparent',
              border: 'none', color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
              borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Recent Signups Tab */}
        {activeTab === 'signups' && (
          <div>
            <h2 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>Recent Signups</h2>
            {recentUsers.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>No users yet</div>
            ) : (
              recentUsers.map(u => (
                <ActivityItem
                  key={u.id}
                  icon={u.role === 'driver' ? '🧑‍✈️' : '🏢'}
                  text={`${u.fullName} signed up as a ${u.role} · ${u.email} · Plan: ${u.planId || 'free'}`}
                  time={timeAgo(u.createdAt)}
                  color={u.role === 'driver' ? '#F97316' : '#3B82F6'}
                />
              ))
            )}
          </div>
        )}

        {/* Function Logs Tab */}
        {activeTab === 'functions' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Cloud Function Activity</h2>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Last 20 events</span>
            </div>
            {logsLoading ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>Loading logs...</div>
            ) : functionLogs.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>
                <div>No function activity yet</div>
                <div style={{ fontSize: 12, marginTop: 8, color: 'var(--text-muted)' }}>Logs appear here when Cloud Functions run</div>
              </div>
            ) : (
              functionLogs.map(log => (
                <FunctionLogItem key={log.id} log={log} />
              ))
            )}
          </div>
        )}
      </div>
    </div>

  );
}